import { EnvironmentProviders, inject, makeEnvironmentProviders, provideEnvironmentInitializer } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Amplify } from 'aws-amplify';
import { cognitoCredentialsProvider, cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';

import { amplifyConfiguration, cognitoConfiguration } from './amplify-configuration';
import { BIOMETRIC_SIGN_IN } from './biometrics/biometric-sign-in';
import { NativeBiometricSignIn } from './biometrics/native-biometric-sign-in';
import { WebBiometricSignIn } from './biometrics/web-biometric-sign-in';
import { AUTH_TELEMETRY } from './telemetry/auth-telemetry';
import { DatadogAuthTelemetry } from './telemetry/datadog-auth-telemetry';
import { SecureStorageVault } from './vault/secure-storage-vault';
import { VAULT } from './vault/vault';
import { VaultTokenStorage } from './vault/vault-token-storage';
import { WebVault } from './vault/web-vault';

/**
 * Binds the auth machine's ports to real implementations.
 */
export function provideAuth(): EnvironmentProviders {
  return makeEnvironmentProviders([
    SecureStorageVault,
    WebVault,
    VaultTokenStorage,
    { provide: AUTH_TELEMETRY, useClass: DatadogAuthTelemetry },
    // There is no Keychain in a browser, so the web build gets `localStorage` and always reports
    // `silent`. That is an accepted risk for an internal-only build, recorded in ADR 0003.
    {
      provide: VAULT,
      useFactory: () => (Capacitor.isNativePlatform() ? inject(SecureStorageVault) : inject(WebVault)),
    },
    NativeBiometricSignIn,
    WebBiometricSignIn,
    // A browser has nowhere safe to keep a password, so the web build reports itself permanently
    // un-enrollable rather than reaching for `WebAuthn` — which proves possession of a device rather
    // than releasing a stored secret, and so cannot do this job at all. See ADR 0003.
    {
      provide: BIOMETRIC_SIGN_IN,
      useFactory: () => (Capacitor.isNativePlatform() ? inject(NativeBiometricSignIn) : inject(WebBiometricSignIn)),
    },
    provideEnvironmentInitializer(() => configureAmplify(inject(VaultTokenStorage))),
  ]);
}

/**
 * Configure Amplify and point its token storage at the Vault, in that order and in one place.
 *
 * Without the storage the design is inert: Amplify keeps its refresh token wherever it defaults to:
 * i.e. `localStorage` in the WebView, which the OS clears — so priming the Vault has no effect on
 * `fetchAuthSession()` and nothing is ever restored from it.
 *
 * **Leaving out the `Auth` library option fails silently.**
 * `Amplify.configure(config)` called with no library options resets the token provider's storage to Amplify's default,
 * undoing any earlier `setKeyValueStorage`. Passing our own provider takes the branch that does not touch storage,
 * which also means `setAuthConfig` becomes ours to call.
 *
 * It runs as an environment initializer rather than from a service constructor so the ordering cannot depend on who
 * injects what first: every Amplify call, in this launch and in every sign-in after it, has to go through this storage.
 */
function configureAmplify(storage: VaultTokenStorage): void {
  cognitoUserPoolsTokenProvider.setAuthConfig(cognitoConfiguration);
  cognitoUserPoolsTokenProvider.setKeyValueStorage(storage);

  Amplify.configure(amplifyConfiguration, {
    Auth: { tokenProvider: cognitoUserPoolsTokenProvider, credentialsProvider: cognitoCredentialsProvider },
  });
}
